import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  PRIMARIES,
  ThemeIds,
  backgroundById,
  primaryReadable,
} from '@porter/shared-theme';
import { resetButton, ring } from './constants';
import { primaryContrastNotice } from './contrast';

interface Props {
  /** The background decides which primaries can also colour headings. */
  theme: ThemeIds;
  primaryId: string | null;
  onSelect: (id: string) => void;
}

function PrimaryPicker({ theme, primaryId, onSelect }: Props) {
  const background = backgroundById(theme.backgroundId);
  const notice = primaryContrastNotice(theme);

  return (
    <>
      <Typography
        variant="body2"
        sx={{ margin: '0 0 12px', color: 'text.secondary' }}>
        Buttons, headers and key UI. Text color is paired for you.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: '10px',
        }}>
        {PRIMARIES.map(primary => {
          const readable =
            !background || primaryReadable(primary.hex, background);
          const card = (
            <Box
              key={primary.id}
              component="button"
              type="button"
              onClick={() => onSelect(primary.id)}
              aria-pressed={primary.id === primaryId}
              sx={{
                ...resetButton,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                '&::after': ring(primary.id === primaryId, 4),
              }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 44,
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  background: primary.hex,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // Authored pairing — never recomputed.
                  color: primary.on,
                  typography: 'caption',
                  fontWeight: 600,
                }}>
                Book
                {!readable && (
                  <InfoOutlinedIcon
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      fontSize: 13,
                      opacity: 0.85,
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                component="p"
                fontWeight={600}
                sx={{ color: 'text.primary' }}>
                {primary.name}
              </Typography>
            </Box>
          );

          if (readable || !background) return card;
          return (
            <Tooltip
              key={primary.id}
              title={`Too close to ${background.name} to read as a heading. Buttons still use it.`}
              placement="top"
              arrow>
              {card}
            </Tooltip>
          );
        })}
      </Box>
      {notice && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
            marginTop: '12px',
            color: 'text.secondary',
          }}>
          <InfoOutlinedIcon sx={{ fontSize: 15, marginTop: '1px' }} />
          <Typography variant="caption" component="p">
            {notice}
          </Typography>
        </Box>
      )}
    </>
  );
}

export default PrimaryPicker;
